import { BrowserProvider } from 'ethers';
import { useEffect, useState } from 'react';

import { waitAsync } from 'lib/promiseTools';
import { staticDispatch } from 'reduxState/store';
import {
  changeWalletChainId,
  updatePendingWalletAddress,
} from 'reduxState/wallet/walletDetailsReducer';

import { EIP6963AnnounceProviderEvent, EIP6963ProviderDetail } from './types';

/** https://eips.ethereum.org/EIPS/eip-6963 */
export function listenForEip6963Web3Wallets(
  callback: (detail: EIP6963AnnounceProviderEvent) => void
) {
  // The DApp listens to announced providers
  window.addEventListener('eip6963:announceProvider' as any, callback);

  // The DApp dispatches a request event which will be heard by
  // Wallet's code that had run earlier
  window.dispatchEvent(new Event('eip6963:requestProvider'));
}

export function stopListeningForEip6963Web3Wallets(
  callback: (detail: EIP6963AnnounceProviderEvent) => void
) {
  window.removeEventListener('eip6963:announceProvider' as any, callback);
}

const lastDiscoveredByRdns: {
  [rdns: string]: EIP6963ProviderDetail;
} = {};

async function discoverEip6963WalletByRdns(
  rdns: string,
  useCached = false
): Promise<EIP6963ProviderDetail> {
  if (useCached && lastDiscoveredByRdns[rdns]) {
    return lastDiscoveredByRdns[rdns];
  }
  return new Promise((resolve, reject) => {
    function onDiscover(event: EIP6963AnnounceProviderEvent) {
      if (event.detail.info.rdns === rdns) {
        stopListeningForEip6963Web3Wallets(onDiscover);
        lastDiscoveredByRdns[event.detail.info.rdns] = event.detail;
        resolve(event.detail);
      }
    }
    listenForEip6963Web3Wallets(onDiscover);
    waitAsync(2000).then(() => {
      stopListeningForEip6963Web3Wallets(onDiscover);
      reject(new Error(`Wallet ${rdns} not detected.`));
    });
  });
}

export function useDiscoverEip6961WalletByRdns(
  rdns: string | undefined,
  useCached = false
) {
  const [wallet, setWallet] = useState<EIP6963ProviderDetail | undefined>(
    useCached && rdns ? lastDiscoveredByRdns[rdns] : undefined
  );
  useEffect(() => {
    if (!rdns) {
      return;
    }
    discoverEip6963WalletByRdns(rdns, useCached).then(setWallet);
  }, [rdns, useCached]);

  return wallet;
}

export async function unlockDiscoveredWalletByRdns(
  rdns: string,
  useCached = false
) {
  const { provider } = await discoverEip6963WalletByRdns(rdns, useCached);
  const ethersProvider = new BrowserProvider(provider);
  const signer = await ethersProvider.getSigner();
  return signer.getAddress();
}

export async function getDiscoveredWalletSigner(
  rdns: string,
  useCached = false
) {
  const walletDetail = await discoverEip6963WalletByRdns(rdns, useCached);
  const provider = new BrowserProvider(walletDetail.provider as any);

  // TODO: ethers issue https://github.com/ethers-io/ethers.js/issues/4334
  const unknownProvider = walletDetail.provider as any;
  if (unknownProvider.on) {
    unknownProvider?.removeAllListeners?.();
    unknownProvider.on('accountsChanged', (accounts: string[]) => {
      const firstWallet = accounts[0];
      if (firstWallet) {
        staticDispatch(updatePendingWalletAddress(firstWallet));
      }
    });

    unknownProvider.on('chainChanged', (chainId: number) => {
      staticDispatch(changeWalletChainId(chainId));
      console.log(
        '[WalletChainChange] Chain change detected - rebuilding for chain id ',
        chainId
      );
      // Make sure we get fresh provider (Unsure if required, but it's bulletproof)
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete lastDiscoveredByRdns[rdns];
    });
  } else {
    console.error('Problem subscribing to wallet events');
  }

  const signer = provider.getSigner();
  return signer;
}
